import { useMutation, useQuery } from "@apollo/client";
import { Grid, useTheme } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Dialog } from "components/Dialog";
import { DateField } from "components/Forms/DateField";
import { ErrorDisplay } from "components/Forms/ErrorDisplay";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { IssueListButton } from "components/Forms/IssueListButton";
import { CatalogLink } from "components/ModelLinks/CatalogLink";
import { ProductPicker } from "components/ProductPicker";
import { SkeletonFormGroup } from "components/Skeleton";
import { StorageLocationLatLong } from "components/StorageLocationLatLong";
import { StorageLocationPicker } from "components/StorageLocationPicker";
import { UnfinishedChangesPrompt } from "components/UnfinishedChangesPrompt";
import { MIN_MEASUREMENT_YEAR } from "encamp-shared/src/constants/tierii";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  FacilityProductMeasurementInput,
  InsideOutside,
  ProductPickerFragment,
  StorageLocationPickerFragment,
  StorageQuadrant,
} from "generated-graphql/graphql";
import { useEndDateShortcuts } from "hooks/useEndDateShortcuts";
import { useValidatingForm } from "hooks/useValidatingForm";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, FormProvider } from "react-hook-form";
import { useParams } from "react-router-dom";
import { hasCriticalIssues } from "util/forms";
import { getStorageQuadrantLabel } from "util/storage";
import { v4 as uuid } from "uuid";
import { useFacility } from "../Facility/useFacility";
import { FACILITY_PRODUCT_MEASUREMENT } from "./Inventory/Facility/schema";
import { PRODUCTS_WITH_MEASUREMENTS } from "./ProductsWithMeasurementsTable";
import { useReport } from "./Report/useReport";
import { useFacilityProductMeasurementInputValidation } from "./Report/validationHooks/useFacilityProductMeasurementInputValidation";

const STORAGE_LOCATION_MEASUREMENT_TOOLTIP = (
  <div>
    <p>
      Storage Locations must be unique and are reported individually per
      chemical in your product.
    </p>
    <p>
      If you wish to record a measurement for a specific vessel at the same
      general location at your facility, include the container ID in the Storage
      Location. For example: {'"'}
      West building (Tank A).{'"'}
    </p>
    <p>
      Recording a second measurement for the same product, at the same location,
      on the same day will cause the second measurement to supersede the first.
    </p>
  </div>
);

type FormType = {
  id: string | null;
  product: ProductPickerFragment | null;
  storageLocation: StorageLocationPickerFragment | null;
  measuredAtUtc: string;
  quantity: number | null;
  endDate?: string | null;
};

const mapFormTypeToFacilityProductMeasurementInput = (
  form: FormType,
  facilityId: string
): FacilityProductMeasurementInput & { id: string } => {
  return {
    id: form.id ?? "",
    facilityId: facilityId,
    productId: form?.product?.id ?? "",
    storageLocation: {
      ...form.storageLocation,
      description: form.storageLocation?.description ?? "",
      facilityId: form.storageLocation?.facilityId ?? "",
      id: form.storageLocation?.id ?? "",
      OR_insideOutside: form?.storageLocation?.OR_insideOutside ?? null,
      NV_insideOutside: form?.storageLocation?.NV_insideOutside ?? null,
    },
    storageLocationId: form?.storageLocation?.id ?? "",
    measuredAtUtc: form.measuredAtUtc,
    quantity: form.quantity,
    endDate: form.endDate ?? null,
  };
};

const UPSERT_FACILITY_PRODUCT_MEASUREMENT_MUTATION = gql(/* GraphQL */ `
  mutation UpsertFacilityProductMeasurement(
    $id: ID!
    $input: FacilityProductMeasurementInput!
  ) {
    upsertFacilityProductMeasurement(id: $id, input: $input) {
      id
    }
  }
`);

const FACILITY_PRODUCT_MEASUREMENT_QUERY = gql(/* GraphQL */ `
  query FacilityProductMeasurement($id: ID!) {
    facilityProductMeasurement(id: $id) {
      id
      measuredAtUtc
      endDate
      quantity
      product {
        ...ProductPicker
      }
      storageLocationId
      storageLocation {
        ...StorageLocationPicker
      }
      issues {
        ...issue
      }
    }
  }
`);

type Props = {
  measurementId?: string;
  facilityId: string;
  onClose: () => void;
  open: boolean;
  product?: Pick<ProductPickerFragment, "id" | "name">;
  /**
   * id of measurement to prefill from
   */
  measurementSeed?: string;
};

export function FacilityProductMeasurementForm(props: Props) {
  const theme = useTheme();
  const alerts = useAlerts();
  const { tenantId } = useParams<{
    tenantId: string;
  }>();

  const {
    onClose,
    measurementId,
    facilityId,
    product: inputProduct,
    measurementSeed,
  } = props;

  const { data: reportData } = useReport();
  const { data: facilityData, loading } = useFacility();

  const facilityState =
    facilityData?.facility?.state ?? reportData?.tierIIReport.facility.state;

  const isOregonFacility = facilityState === "OR";
  const isCaliforniaFacility = facilityState === "CA";

  const { data, loading: measurementLoading } = useQuery(
    FACILITY_PRODUCT_MEASUREMENT_QUERY,
    {
      variables: { id: measurementId ?? measurementSeed ?? "" },
      skip: !measurementId && !measurementSeed,
    }
  );
  const measurement = data?.facilityProductMeasurement;

  const defaultValues: FormType = useMemo(() => {
    return {
      id: measurementSeed ? null : measurement?.id ?? null, // if using a seed, we don't want to pre-fill the id
      facilityId,
      product: inputProduct ?? measurement?.product ?? null,
      storageLocation: {
        ...measurement?.storageLocation,
        description: measurement?.storageLocation?.description ?? "",
        facilityId: measurement?.storageLocation?.facilityId ?? "",
        id: measurement?.storageLocation?.id ?? "",
        latitude:
          measurement?.storageLocation?.latitude ??
          facilityData?.facility?.latitude,
        longitude:
          measurement?.storageLocation?.longitude ??
          facilityData?.facility?.longitude,
      },
      measuredAtUtc:
        measurementSeed || !measurement
          ? DateTime.fromJSDate(new Date()).toISODate()
          : measurement?.measuredAtUtc,
      quantity: measurement?.quantity ?? null,
      endDate: measurement?.endDate ?? null,
    };
  }, [
    measurementSeed,
    measurement,
    facilityId,
    inputProduct,
    facilityData?.facility?.latitude,
    facilityData?.facility?.longitude,
  ]);

  const validator = useFacilityProductMeasurementInputValidation();
  const form = useValidatingForm<FormType>(
    defaultValues,
    measurementSeed ? [] : data?.facilityProductMeasurement?.issues,
    async (input: FormType) => {
      const mapped = mapFormTypeToFacilityProductMeasurementInput(
        input,
        facilityId
      );
      return await validator(mapped);
    }
  );
  const {
    handleSubmit,
    control,
    reset,
    formState,
    issues,
    watch,
    setValue,
    trigger,
  } = form;

  const { errors } = formState;

  const [mutate, { loading: saving }] = useMutation(
    UPSERT_FACILITY_PRODUCT_MEASUREMENT_MUTATION,
    {
      refetchQueries: [
        PRODUCTS_WITH_MEASUREMENTS,
        FACILITY_PRODUCT_MEASUREMENT,
      ],
    }
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const onSubmit = useCallback(
    async (data: FormType) => {
      const input = mapFormTypeToFacilityProductMeasurementInput(
        data,
        facilityId
      );
      try {
        await mutate({
          variables: {
            id: input.id && input.id !== "" ? input.id : uuid(),
            input: {
              facilityId: input.facilityId,
              quantity: Number(input.quantity),
              productId: input.productId,
              measuredAtUtc: input.measuredAtUtc,
              storageLocation: input.storageLocation,
              storageLocationId: input.storageLocationId,
              endDate: input.endDate,
            },
          },
        });

        alerts.success("Successfully saved measurement data");
        handleClose();
      } catch (err) {
        alerts.error("An error occurred while saving measurement data", err);
      }
    },
    [mutate, alerts, handleClose, facilityId]
  );
  const product = watch("product");
  const measuredAtUtc = watch("measuredAtUtc");

  const endDateShortcuts = useEndDateShortcuts(measuredAtUtc);

  const endDateReferenceDate = useMemo(
    () => (measuredAtUtc ? DateTime.fromISO(measuredAtUtc) : undefined),
    [measuredAtUtc]
  );

  const hasPendingChanges = formState.isDirty && !formState.isSubmitSuccessful;

  const [showConfirmClose, setShowConfirmClose] = useState(false);

  const onCancel = useCallback(() => {
    // If there are pending changes, verify the user wants to close the dialog
    if (hasPendingChanges) {
      setShowConfirmClose(true);
      return;
    }

    reset();
    handleClose();
  }, [handleClose, reset, hasPendingChanges]);

  useEffect(() => {
    if (measurement) {
      trigger();
    }
  }, [trigger, measurement]);

  return (
    <>
      <Dialog
        open={props.open}
        onClose={onCancel}
        fullWidth
        title={`${measurementId ? "Edit" : "Add"} Product Measurement`}
        description="Product measurements remain relevant until another
      measurement is made for the same product at the same
      Storage Location. If a product is no longer present at a
      storage location, enter '0' for quantity on hand on the
      date it was removed."
        primaryActionButtonProps={{
          onClick: handleSubmit(onSubmit),
          loading: saving || measurementLoading,
          disabled: hasCriticalIssues(issues),
        }}
        additionalPrimaryActionElements={[
          <IssueListButton issues={issues} key="issues" />,
        ]}
      >
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {measurementLoading || loading ? (
              <SkeletonFormGroup count={8} />
            ) : (
              <Grid
                container
                columnSpacing={theme.spacing(3)}
                rowSpacing={theme.spacing(1)}
              >
                <Grid size={6} sx={{ marginTop: theme.spacing(1) }}>
                  <Controller
                    name="product"
                    control={control}
                    render={({ field, fieldState }) => (
                      <ProductPicker
                        {...field}
                        {...fieldState}
                        defaultSearchTerm={`tenantId:${tenantId}`}
                        required
                        readOnly={!!inputProduct || !!measurementId}
                      />
                    )}
                  />
                </Grid>

                <Grid size={6} sx={{ marginTop: theme.spacing(1) }}>
                  <FormTextField
                    name="quantity"
                    label="Quantity on Hand"
                    control={control}
                    intOnly
                    textFieldProps={{ required: true }}
                  />
                </Grid>

                <Grid size={6}>
                  <DateField
                    label="Measurement Date"
                    control={control}
                    name="measuredAtUtc"
                    datePickerProps={{
                      minDate: DateTime.fromObject({
                        year: MIN_MEASUREMENT_YEAR,
                        month: 1,
                        day: 1,
                      }),
                      maxDate: DateTime.now(),
                    }}
                    required
                    hideErrors
                  />
                </Grid>

                <Grid size={6}>
                  <DateField
                    label="End Date (inclusive)"
                    control={control}
                    name={"endDate"}
                    tooltip="If set, this measurement applies through this date (inclusive). Example: a measurement on 10/01/2025 with End Date 10/31/2025 applies to all days Oct 1-31. Leave blank to continue until the next measurement."
                    datePickerProps={{
                      referenceDate: endDateReferenceDate,
                    }}
                    shortcuts={endDateShortcuts}
                    hideErrors
                  />
                </Grid>

                <Grid size={12} sx={{ pt: "0!important" }}>
                  <ErrorDisplay
                    errors={errors}
                    fields={["measuredAtUtc", "endDate"]}
                  />
                </Grid>

                <Grid size={12}>
                  <Controller
                    name="storageLocation.description"
                    control={control}
                    render={({ field, fieldState }) => (
                      <StorageLocationPicker
                        disableClearable
                        facilityId={facilityId}
                        error={fieldState.error}
                        description={field.value}
                        onSelectLocation={(location) => {
                          setValue(
                            "storageLocation",
                            {
                              ...location,
                              latitude:
                                location.latitude ??
                                facilityData?.facility?.latitude,
                              longitude:
                                location.longitude ??
                                facilityData?.facility?.longitude,
                            },
                            {
                              shouldValidate: true,
                            }
                          );
                        }}
                        required
                        tooltip={STORAGE_LOCATION_MEASUREMENT_TOOLTIP}
                      />
                    )}
                  />
                </Grid>
                <Grid size={12}>
                  <StorageLocationLatLong facilityState={facilityState} />
                </Grid>
                <Grid size={12}>
                  {isOregonFacility && (
                    <>
                      <Grid size={12}>
                        <FormSelect
                          name="storageLocation.OR_insideOutside"
                          label="Inside/Outside *"
                          selectItems={Object.values(InsideOutside).map(
                            (insideOutside) => ({
                              display: prettyPrintEnumValue(insideOutside),
                              value: insideOutside,
                            })
                          )}
                          control={control}
                        />
                      </Grid>
                      <Grid size={12}>
                        <FormTextField
                          label="Storage Building *"
                          name="storageLocation.OR_storageBuilding"
                          control={control}
                        />
                      </Grid>
                      <Grid size={12}>
                        <FormTextField
                          label="Storage Floor"
                          name="storageLocation.OR_storageFloor"
                          control={control}
                        />
                      </Grid>
                      <Grid size={12}>
                        <FormSelect
                          name="storageLocation.OR_storageQuadrant"
                          label="Storage Quadrant *"
                          selectItems={Object.values(StorageQuadrant).map(
                            (storageQuadrant) => ({
                              display: getStorageQuadrantLabel(storageQuadrant),
                              value: storageQuadrant,
                            })
                          )}
                          control={control}
                        />
                      </Grid>
                      <Grid size={12}>
                        <FormTextField
                          label="Storage Room"
                          name="storageLocation.OR_storageRoom"
                          control={control}
                        />
                      </Grid>
                      <Grid size={12}>
                        <FormTextField
                          label="Storage Area *"
                          name="storageLocation.OR_storageArea"
                          control={control}
                        />
                      </Grid>
                    </>
                  )}
                  {facilityState === "NV" && (
                    <>
                      <Grid size={12}>
                        <FormSelect
                          name="storageLocation.NV_insideOutside"
                          label="Inside/Outside"
                          selectItems={Object.values(InsideOutside).map(
                            (insideOutside) => ({
                              display: prettyPrintEnumValue(insideOutside),
                              value: insideOutside,
                            })
                          )}
                          control={control}
                        />
                      </Grid>
                    </>
                  )}
                  {isCaliforniaFacility && (
                    <>
                      <Grid size={12}>
                        <FormTextField
                          label="Grid Number"
                          name="storageLocation.CA_gridNumber"
                          control={control}
                        />
                      </Grid>
                      <Grid size={12}>
                        <FormTextField
                          label="Map Number"
                          name="storageLocation.CA_mapNumber"
                          control={control}
                        />
                      </Grid>
                    </>
                  )}
                  <CatalogLink
                    id={product?.id ?? ""}
                    type="Product"
                    name={product?.name ?? ""}
                    issues={issues ?? []}
                  />
                </Grid>
              </Grid>
            )}
          </form>
        </FormProvider>
      </Dialog>
      <UnfinishedChangesPrompt when={hasPendingChanges} />
      <ConfirmDialog
        open={showConfirmClose}
        onConfirm={onClose}
        onClose={() => setShowConfirmClose(false)}
        header="Are you sure you want to leave? You have unsaved changes."
        confirmText="Yes, I'm Sure"
      />
    </>
  );
}
